/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  appLanguageSelector,
  createRydeColumnHelper,
  fixedColumnNamesAF,
  gridControlsSF,
  IColumnDef,
  IRideTableDto,
} from "@ryde/shared";

import { colHandlingRidesObj } from "../../cells";
import { getFixedColumnsNamesFromTableDef } from "./helpers";
import { idEmptyColumnObj, useIdColumnObj } from "./table-def.columns.fixed";
import { useDynamicTableDefColumns } from "./use-table-def.dynamic-columns";

const columnHelper = createRydeColumnHelper<IRideTableDto>();

interface IUseTableDefsRide {
  tableDefsRides: IColumnDef<IRideTableDto, any>[];
  columnKeys: string[];
}

export const useTableDefsRides = (): IUseTableDefsRide => {
  const { entity } = usePageContext();
  const language = useRecoilValue(appLanguageSelector);
  const gridControls = useRecoilValue(gridControlsSF(entity));
  const { dynamicColumns } = useDynamicTableDefColumns({
    genericColumnHelper: columnHelper,
    gridControls,
  });
  const idColumnObj = useIdColumnObj<IRideTableDto>();

  const setFixedColumnNames = useSetRecoilState(fixedColumnNamesAF(entity));
  const columnKeysSerialized = useMemo(() => {
    return [...(dynamicColumns || [])].map(x => x.id).join(".");
  }, [dynamicColumns]);

  const tableDefsRides: IColumnDef<IRideTableDto, any>[] = useMemo(() => {
    if (!dynamicColumns.length) {
      return [];
    }

    return [
      columnHelper.display(idEmptyColumnObj()),
      columnHelper.display(colHandlingRidesObj),
      columnHelper.accessor((x: any) => x["id"], idColumnObj),
      ...dynamicColumns,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeysSerialized, language]);

  const columnKeys = useMemo(
    () => tableDefsRides.filter(x => x.id).map(x => x.id || ""),
    [tableDefsRides],
  );

  const fixedColumnNames = useMemo(() => {
    return getFixedColumnsNamesFromTableDef(tableDefsRides);
  }, [tableDefsRides]);

  useEffect(() => {
    setFixedColumnNames(fixedColumnNames);
  }, [fixedColumnNames, setFixedColumnNames]);

  return { tableDefsRides, columnKeys };
};
